import { useMemo, useState } from 'react';
import { useSession } from './SessionProvider.js';
import { TopBar, ShellFooter } from './TopBar.js';
import { JobShell } from './JobShell.js';
import { useUploadQueue } from '../capture/useUploadQueue.js';
import { supabase } from '../lib/supabase.js';
import {
  CatalogView, CustomersView, DashboardView, JobsView, RatesView,
} from '../screens/connected.js';
import { useUsingFixtures } from '../data/RepoProvider.js';
import { Admin } from '../screens/Admin.js';
import type { RateProfile } from '../calc/types.js';

type ShopView = 'dash' | 'jobs' | 'customers' | 'catalog' | 'rates' | 'admin';

/**
 * The shop.
 *
 * Two levels of navigation, because a shop and a job are different contexts.
 * Open a job and `JobShell` takes over the whole screen — mounted with
 * `key={reference}` so no state from the previous job can survive into it.
 */
export function Shell() {
  const { session, t } = useSession();
  const usingFixtures = useUsingFixtures();
  const [view, setView] = useState<ShopView>('dash');
  const [openJob, setOpenJob] = useState<string | null>(null);
  /**
   * A part name to open the catalog on.
   *
   * Set when an estimator clicks the "catalog" badge on a priced line: they are
   * asking *which row did this come from* — a question no amount of badge can
   * answer, because the answer is a row with a make, a body style and three
   * times on it.
   */
  const [catalogFocus, setCatalogFocus] = useState<string | null>(null);

  // Uploads go through the queue, always — including on a good connection. A
  // path that only runs on a bad connection is a path only tested on one.
  const uploadDeps = useMemo(() => ({
    async putObject(path: string, blob: Blob, contentType: string) {
      const { error } = await supabase.storage.from('photos')
        .upload(path, blob, { contentType, upsert: true });
      return error ? error.message : null;
    },
    async insertRow(row: {
      id: string; shopId: string; jobId: string; slot: string;
      storagePath: string; width: number; height: number; takenAt: string;
    }) {
      const { error } = await supabase.from('photos').insert({
        id: row.id, shop_id: row.shopId, job_id: row.jobId, slot: row.slot,
        storage_path: row.storagePath, width: row.width, height: row.height,
        taken_at: row.takenAt,
      });
      return error ? error.message : null;
    },
    newId: () => crypto.randomUUID(),
  }), []);
  const queue = useUploadQueue(uploadDeps);

  if (!session) return null;
  const { can } = session;

  if (openJob !== null) {
    return (
      <JobShell key={openJob} reference={openJob}
                onBack={() => setOpenJob(null)} queue={queue}
                onOpenCatalog={(partName) => {
                  // Leaves the job. The draft is already saved, so nothing is
                  // lost — that is what makes this safe to offer mid-estimate.
                  setCatalogFocus(partName);
                  setOpenJob(null);
                  setView('catalog');
                }} />
    );
  }

  const shopNav: Array<[ShopView, string]> = [
    ['dash', t('Dashboard')], ['jobs', t('Jobs')], ['customers', t('Customers')],
    ['catalog', t('Catalog')], ['rates', t('Rates')],
  ];

  return (
    <div className="shell">
      <TopBar nav={
        <>
          {shopNav.map(([id, label]) => (
            <button key={id} aria-current={view === id} onClick={() => setView(id)}>
              {label}
            </button>
          ))}
          {can.manageUsers && (
            <button aria-current={view === 'admin'} onClick={() => setView('admin')}>
              {t('Admin')}
            </button>
          )}
        </>
      } />

      <div className="body wide">
        <main className="shell-main">
          {view === 'dash' && <DashboardView />}
          {view === 'jobs' && <JobsView onOpen={(ref) => setOpenJob(ref)} />}
          {view === 'customers' && <CustomersView />}
          {view === 'catalog' && (
            <CatalogView focus={catalogFocus}
                         onFocusHandled={() => setCatalogFocus(null)} />
          )}
          {view === 'rates' && <RatesView />}
          {view === 'admin' && <Admin />}
        </main>
      </div>

      <ShellFooter usingFixtures={usingFixtures} />
    </div>
  );
}
